import { useParams, Navigate, Link } from "@/lib/router-compat";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import BreadcrumbNav from "@/components/BreadcrumbNav";
import ArticleCard from "@/components/ArticleCard";
import GooglePreferredSource from "@/components/GooglePreferredSource";
import { getArticleBySlug, getRelatedArticles } from "@/data/articles";
import { useEffect } from "react";

const Articulo = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;
  const related = slug ? getRelatedArticles(slug) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Article structured data
  useEffect(() => {
    if (!article) return;
    const existing = document.querySelector('script[data-article-ld]');
    if (existing) existing.remove();

    const ld = {
      "@context": "https://schema.org",
      "@type": "Article",
      headline: article.title,
      description: article.description,
      datePublished: article.date,
      dateModified: article.date,
      author: { "@type": "Organization", name: "TiemposHoy.com" },
      publisher: {
        "@type": "Organization",
        name: "TiemposHoy.com",
        url: "https://tiemposhoy.com",
      },
      mainEntityOfPage: `https://tiemposhoy.com/articulos/${article.slug}`,
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.setAttribute("data-article-ld", "true");
    script.text = JSON.stringify(ld);
    document.head.appendChild(script);

    return () => {
      const s = document.querySelector("script[data-article-ld]");
      if (s) s.remove();
    };
  }, [article]);

  if (!article) return <Navigate to="/articulos" replace />;

  const formattedDate = new Date(article.date + "T12:00:00").toLocaleDateString("es-ES", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <>
      <SEOHead
        title={article.title}
        description={article.description}
        keywords={article.keywords}
        canonicalUrl={`https://tiemposhoy.com/articulos/${article.slug}`}
      />

      <div className="min-h-screen flex flex-col">
        <Navigation />
        <main className="flex-1 container mx-auto px-4 py-8">
          <BreadcrumbNav
            items={[
              { label: "Artículos", href: "/articulos" },
              { label: article.title },
            ]}
          />

          <article className="max-w-3xl mx-auto">
            <header className="mb-8">
              <h1 className="text-3xl md:text-4xl font-bold mb-3">{article.title}</h1>
              <div className="flex items-center gap-4 text-sm text-muted-foreground">
                <time dateTime={article.date}>{formattedDate}</time>
                <span>·</span>
                <span>{article.readTime} de lectura</span>
              </div>
            </header>

            <div className="prose prose-lg max-w-none">
              {article.content.map((section, i) => (
                <section key={i}>
                  {section.heading && <h2>{section.heading}</h2>}
                  {section.paragraphs.map((p, j) => (
                    <p key={j} className="whitespace-pre-line">{p}</p>
                  ))}
                </section>
              ))}
            </div>

            {/* Internal links CTA */}
            <div className="mt-12 p-6 bg-muted/50 rounded-lg border text-center">
              <p className="font-semibold text-lg mb-3">¿Quieres ver los resultados de hoy?</p>
              <div className="flex justify-center gap-3 flex-wrap">
                <Link to="/tiempos-nica-hoy" className="inline-flex items-center gap-1 text-primary hover:underline font-medium">
                  🇳🇮 Nica Hoy
                </Link>
                <Link to="/tiempos-tica-hoy" className="inline-flex items-center gap-1 text-primary hover:underline font-medium">
                  🇨🇷 Tica Hoy
                </Link>
              </div>
            </div>

            {/* Google: agregar como fuente preferida */}
            <GooglePreferredSource />
          </article>

          {/* Related articles */}
          {related.length > 0 && (
            <section className="mt-16 max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-6">Artículos Relacionados</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {related.map((a) => (
                  <ArticleCard key={a.slug} {...a} />
                ))}
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
};

export default Articulo;
